import React, { FC, memo, useMemo } from 'react';
import { useGetCurrencySymbol } from 'app/appState';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { getSizeCodeTypeString } from 'entities/SizeCode';
import { getPriceChangeParams, getSelectedSizeCodes } from '../../model/selectors';
import { PriceChangeDirection, RateType } from '../../model/types';
import { getAdjustedRate } from '../../utils/helpers/getAdjustedRate';

const RATE_ORDER: RateType[] = [RateType.DAILY_RATE, RateType.WEEK_RATE, RateType.MONTH_RATE];

export const PricesPreview: FC = memo(() => {
  const { t } = useAppTranslation('boxes');
  const currencySymbol = useGetCurrencySymbol();

  const selectedSizeCodes = useAppSelector(getSelectedSizeCodes);
  const { direction, percent, rateTypes } = useAppSelector(getPriceChangeParams);

  const rateLabels: Record<RateType, string> = useMemo(
    () => ({
      [RateType.DAILY_RATE]: t('Day'),
      [RateType.WEEK_RATE]: t('Week'),
      [RateType.MONTH_RATE]: t('Month'),
    }),
    [t],
  );

  const sortedSizeCodes = useMemo(() => [...selectedSizeCodes].sort((a, b) => a.square - b.square), [selectedSizeCodes]);

  const selectedRateTypes = RATE_ORDER.filter((rateType) => rateTypes.includes(rateType));

  const formatRate = (rate: Nullable<number> | undefined): string => {
    if (rate === null || rate === undefined) {
      return '—';
    }

    return `${rate} ${currencySymbol}`;
  };

  return (
    <div className="flex flex-col bg-secondaryLight rounded-lg p-4 max-h-[460px] overflow-y-auto">
      <div className="text-accent font-semibold text-base mb-3">{t('Preview')}</div>

      {sortedSizeCodes.map((sizeCode) => (
        <div key={sizeCode.sizeCodeId} className="mb-4 last:mb-0">
          <div className="font-semibold text-primary mb-1">
            {sizeCode.square} {getSizeCodeTypeString(sizeCode.sizeCodeType)}
          </div>

          {selectedRateTypes.map((rateType) => {
            const currentRate = sizeCode[rateType];
            const newRate = percent ? getAdjustedRate(currentRate, percent, direction) : null;
            const isIncrease = direction === PriceChangeDirection.INCREASE;

            return (
              <div key={rateType} className="flex items-center justify-between gap-3 text-sm py-0.5">
                <span className="text-primaryLight w-16">{rateLabels[rateType]}</span>
                <span className="flex items-center gap-2 text-primary">
                  <span>{formatRate(currentRate)}</span>
                  {newRate !== null && (
                    <>
                      <span className="text-primaryLight">→</span>
                      <span className={isIncrease ? 'text-success font-semibold' : 'text-error font-semibold'}>{formatRate(newRate)}</span>
                    </>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
});
